import { z } from 'zod';

import type { ZodUseMutation, ZodUseQuery } from '@/utils/zodReactQuery';

/**
 * The wire contract with the backend uses `snake_case` keys, which MSW
 * reproduces byte for byte (see `mocks/handlers/admin/playground/`). These
 * types describe the same payload **after** the shared `AGENDA_PRO_API`
 * client's `useCamelCase: true` transform, which every other service in this
 * repo already relies on — so the keys here are `camelCase`, matching
 * `Conversations.schema.ts` / `Evaluations.schema.ts`, not the wire
 * contract's literal field names. Verified empirically: `API.post` sends a
 * `camelCase` body as `snake_case` over the wire and parses a `snake_case`
 * response back into `camelCase` — the two are not implicitly equal.
 */
export const graphVersionSchema = z.enum(['v1', 'v2']);
export type GraphVersion = z.infer<typeof graphVersionSchema>;

export const playgroundChannelSchema = z.enum(['conversation', 'whatsapp', 'email']);
export type PlaygroundChannel = z.infer<typeof playgroundChannelSchema>;

/**
 * `accountSnapshot`'s own keys stay `snake_case` regardless of this
 * transform — it is opaque data matching `AccountContext.to_dict()`, never
 * read or written key-by-key by front code, so the recursive camel/snake
 * conversion round-trips it unchanged only as long as it is authored in
 * `snake_case` to begin with (see `src/utils/snapshotPresets/`).
 */
export type PlaygroundAskRequest = {
	question: string;
	threadId?: string;
	graphVersion?: GraphVersion | null;
	accountSnapshot?: Record<string, unknown> | null;
	country?: string;
	companyId?: string;
	channel?: PlaygroundChannel;
	/** Only meaningful when `graphVersion` is `'v2'` — omitted otherwise (`front#SPEC-007`). */
	area?: string | null;
};

export const graphVersionSourceSchema = z.enum(['explicit', 'rule', 'ramp', 'default', 'sticky']);
export type GraphVersionSource = z.infer<typeof graphVersionSourceSchema>;

export const playgroundAskResponseSchema = z.object({
	reply: z.string(),
	threadId: z.string(),
	graphVersion: graphVersionSchema,
	graphVersionSource: graphVersionSourceSchema,
	matchedRule: z.string().nullable(),
	shortcircuit: z.string().nullable(),
	wouldHandoffTo: z.string().nullable(),
	traceId: z.string(),
	/** Which area answered the turn — `null` on `v1` (`front#SPEC-007`). */
	area: z.string().nullable(),
});
export type PlaygroundAskResponse = z.infer<typeof playgroundAskResponseSchema>;

export const graphRoutingRuleSchema = z.object({
	id: z.string(),
	tier: z.array(z.number().int()).optional(),
	companyIds: z.array(z.string()).optional(),
	version: graphVersionSchema,
});
export type GraphRoutingRule = z.infer<typeof graphRoutingRuleSchema>;

/** An area the `v2` router can dispatch a turn to (`front#SPEC-007`). */
export const graphRoutingAreaSchema = z.object({
	name: z.string(),
	description: z.string(),
	capabilities: z.array(z.string()),
});
export type GraphRoutingArea = z.infer<typeof graphRoutingAreaSchema>;

export const graphRoutingResponseSchema = z.object({
	default: graphVersionSchema,
	versions: z.array(graphVersionSchema),
	rules: z.array(graphRoutingRuleSchema),
	ramp: z.record(graphVersionSchema, z.number()),
	areas: z.array(graphRoutingAreaSchema),
});
export type GraphRoutingResponse = z.infer<typeof graphRoutingResponseSchema>;

export const graphRoutingResolveResponseSchema = z.object({
	companyId: z.string(),
	tier: z.number().int().nullable(),
	graphVersion: graphVersionSchema,
	matchedRule: z.string().nullable(),
});
export type GraphRoutingResolveResponse = z.infer<typeof graphRoutingResolveResponseSchema>;

export type UseAskPlayground = ZodUseMutation<PlaygroundAskResponse, PlaygroundAskRequest>;
export type UseGraphRouting = ZodUseQuery<GraphRoutingResponse, never>;
export type UseResolveGraphVersion = ZodUseQuery<GraphRoutingResolveResponse, { companyId: string }>;
